/**
 * Social video formats, and the parts of a vertical video that the apps cover with their own
 * buttons and captions ("safe zones"). The zones are approximations of what TikTok, Instagram
 * Reels and YouTube Shorts draw over a 1080×1920 video, as fractions of its size.
 */

export const SOCIAL_FORMATS = [
  { id: 'vertical', width: 1080, height: 1920, ratio: '9:16' },
  { id: 'portrait', width: 1080, height: 1350, ratio: '4:5' },
  { id: 'square', width: 1080, height: 1080, ratio: '1:1' },
  { id: 'wide', width: 1920, height: 1080, ratio: '16:9' },
] as const;
export type SocialFormatId = (typeof SOCIAL_FORMATS)[number]['id'];

export const SAFE_ZONES = {
  tiktok: { top: 160 / 1920, bottom: 480 / 1920, left: 60 / 1080, right: 140 / 1080 },
  reels: { top: 220 / 1920, bottom: 420 / 1920, left: 40 / 1080, right: 120 / 1080 },
  shorts: { top: 240 / 1920, bottom: 400 / 1920, left: 48 / 1080, right: 190 / 1080 },
} as const;
export type SafeZoneId = keyof typeof SAFE_ZONES;

/**
 * Margins the ready-made templates keep on vertical videos. As much room as the union of the
 * apps' safe zones takes, but split evenly between opposite sides, so layouts sit in the middle
 * of the frame instead of being pushed up and left. The editor still shows each app's real zones.
 */
export const VERTICAL_TEMPLATE_MARGINS = (() => {
  const max = (k: 'top' | 'bottom' | 'left' | 'right') =>
    Math.max(...Object.values(SAFE_ZONES).map((s) => s[k]));
  const v = (max('top') + max('bottom')) / 2;
  const h = (max('left') + max('right')) / 2;
  return { top: v, bottom: v, left: h, right: h };
})();

/** Vertical enough for the apps' full-screen players. */
export function isVerticalVideo(width: number, height: number): boolean {
  return height / width >= 1.6;
}

/** The part of a `width`×`height` video no app covers. */
export function safeRect(zone: SafeZoneId, width: number, height: number) {
  const z = SAFE_ZONES[zone];
  const x = Math.round(z.left * width);
  const y = Math.round(z.top * height);
  return {
    x,
    y,
    width: Math.round(width - x - z.right * width),
    height: Math.round(height - y - z.bottom * height),
  };
}
